import React, { useState, useEffect } from "react";
import { useAuth } from "../../lib/auth";
import { useContent, persianSlug, uid } from "../../lib/content";
import { toPersianDigits, parseHashtags, getImageUrl } from "../../lib/api";
import { uploadImage } from "../../lib/storage";
import { Pencil, Trash2, Plus, X, Save, Upload } from "lucide-react";

const empty = {
  id: "",
  slug: "",
  title: "",
  excerpt: "",
  content: "",
  cover_image: "",
  author: "تیم تحسین",
  tags: [],
  published: true,
};

export default function AdminArticles() {
  const { r2 } = useAuth();
  const { articles, saveArticles } = useContent();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [tagsInput, setTagsInput] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && editing) close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  // Close modal if the item being edited was removed externally
  useEffect(() => {
    if (editing && editing !== "new" && !articles.some((a) => a.id === editing.id)) {
      close();
    }
  }, [articles]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setEditing("new");
    setForm({ ...empty });
    setTagsInput("");
    setError("");
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ ...a });
    setTagsInput((a.tags || []).join(", "));
    setError("");
  };
  const close = () => {
    setEditing(null);
    setForm(empty);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const tags = parseHashtags(tagsInput);
      const isNew = editing === "new";
      const id = isNew ? uid() : editing.id;
      const slug = form.slug?.trim() || persianSlug(form.title, id);
      const slugExists = articles.some((a) => a.slug === slug && a.id !== id);
      const finalSlug = slugExists ? `${slug}-${id.slice(-4)}` : slug;

      const next = {
        ...empty,
        ...form,
        id,
        slug: finalSlug,
        tags,
        published: !!form.published,
        created_at: isNew ? new Date().toISOString() : editing.created_at,
        updated_at: new Date().toISOString(),
      };

      const list = isNew ? [next, ...articles] : articles.map((a) => (a.id === id ? next : a));
      await saveArticles(r2, list);
      close();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`آیا از حذف «${a.title}» مطمئن هستید؟`)) return;
    try {
      const list = articles.filter((x) => x.id !== a.id);
      await saveArticles(r2, list);
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    const resizeOpts = { maxWidth: 1600, maxHeight: 1200, quality: 0.82 };
    try {
      const res = await uploadImage(r2, file, "images", resizeOpts);
      setForm((f) => ({ ...f, cover_image: { url: res.url, thumb: res.thumbUrl } }));
    } catch (e) {
      setError(`آپلود ناموفق: ${e.message}`);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid="admin-articles-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl font-bold text-ink">
          مقالات ({toPersianDigits(articles.length)})
        </h2>
        <button
          onClick={openNew}
          className="inline-flex items-center gap-2 bg-honey-500 hover:bg-honey-600 text-white font-display px-5 py-2.5 rounded-full"
          data-testid="admin-article-new-btn"
        >
          <Plus className="w-4 h-4" /> مقاله جدید
        </button>
      </div>

      <div className="bg-white border border-honey-200 rounded-2xl overflow-hidden">
        <table className="w-full text-right">
          <thead className="bg-honey-50">
            <tr className="text-sm text-inkMuted font-display">
              <th className="px-5 py-3 font-bold">عنوان</th>
              <th className="px-5 py-3 font-bold">نویسنده</th>
              <th className="px-5 py-3 font-bold">وضعیت</th>
              <th className="px-5 py-3 font-bold">عملیات</th>
            </tr>
          </thead>
          <tbody>
            {articles.map((a) => (
              <tr key={a.id} className="border-t border-honey-100 text-sm">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-honey-100 shrink-0">
                      {a.cover_image && (
                        <img src={getImageUrl(a.cover_image, "thumb")} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div>
                      <div className="font-display font-bold text-ink">{a.title}</div>
                      <div className="text-xs text-inkMuted">{a.slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 text-inkMuted">{a.author}</td>
                <td className="px-5 py-3">
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      a.published
                        ? "bg-olive-100 text-olive-600"
                        : "bg-honey-100 text-honey-700"
                    }`}
                  >
                    {a.published ? "منتشر شده" : "پیش‌نویس"}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <button
                    onClick={() => openEdit(a)}
                    className="inline-flex items-center gap-1 text-honey-600 hover:text-honey-700 ml-3 font-display"
                    data-testid={`admin-article-edit-${a.slug}`}
                  >
                    <Pencil className="w-4 h-4" /> ویرایش
                  </button>
                  <button
                    onClick={() => remove(a)}
                    className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 font-display"
                    data-testid={`admin-article-delete-${a.slug}`}
                  >
                    <Trash2 className="w-4 h-4" /> حذف
                  </button>
                </td>
              </tr>
            ))}
            {articles.length === 0 && (
              <tr>
                <td colSpan={4} className="px-5 py-10 text-center text-inkMuted">
                  هنوز مقاله‌ای ندارید.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-ink/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          data-testid="admin-article-modal"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <form
            onSubmit={submit}
            className="w-full max-w-3xl bg-paper rounded-3xl shadow-xl my-8 overflow-hidden"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-honey-200 bg-white">
              <h3 className="font-display text-lg font-bold text-ink">
                {editing === "new" ? "افزودن مقاله" : "ویرایش مقاله"}
              </h3>
              <button
                type="button"
                onClick={close}
                className="p-2 rounded-full hover:bg-honey-100"
                aria-label="بستن"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <F label="عنوان *" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="admin-article-title" />
              <F label="Slug (اختیاری)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} placeholder="خودکار از عنوان" />
              <F label="نویسنده" value={form.author} onChange={(v) => setForm({ ...form, author: v })} />
              <div>
                <label className="block text-sm font-display text-inkMuted mb-2">چکیده</label>
                <textarea
                  rows={2}
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-honey-200 bg-white focus:border-honey-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-display text-inkMuted mb-2">تصویر کاور</label>
                <div className="flex items-center gap-3">
                  {form.cover_image && (
                    <div className="w-28 h-20 rounded-xl overflow-hidden border border-honey-200">
                      <img src={getImageUrl(form.cover_image, "thumb")} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <label className="inline-flex items-center gap-2 cursor-pointer bg-white border border-honey-300 hover:border-honey-500 rounded-xl px-4 py-2.5 text-sm">
                    <Upload className="w-4 h-4" />
                    {uploading ? "در حال آپلود..." : "آپلود تصویر"}
                    <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} data-testid="admin-article-cover-upload" />
                  </label>
                </div>
                <p className="text-xs text-inkMuted mt-2">یا URL مستقیم در فیلد زیر وارد کنید:</p>
                <input
                  type="url"
                  value={typeof form.cover_image === "string" ? form.cover_image : form.cover_image?.url || ""}
                  onChange={(e) => setForm({ ...form, cover_image: e.target.value ? { url: e.target.value, thumb: e.target.value } : "" })}
                  placeholder="https://..."
                  className="mt-2 w-full px-4 py-2.5 rounded-xl border border-honey-200 bg-white focus:border-honey-500 focus:outline-none font-mono text-sm"
                  data-testid="admin-article-cover-url"
                />
              </div>

              <div>
                <label className="block text-sm font-display text-inkMuted mb-2">محتوا (Markdown ساده) *</label>
                <textarea
                  required
                  rows={10}
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-honey-200 bg-white focus:border-honey-500 focus:outline-none font-mono text-sm leading-relaxed"
                  data-testid="admin-article-content"
                />
                <p className="text-xs text-inkMuted mt-1">پشتیبانی از: # ## ### ، لیست (- ) و **ضخیم**</p>
              </div>

              <F label="برچسب‌ها (با کاما)" value={tagsInput} onChange={setTagsInput} />

              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="admin-article-published" />
                <span>منتشر شده</span>
              </label>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-white border-t border-honey-200 flex justify-end gap-2">
              <button type="button" onClick={close} className="px-5 py-2.5 rounded-full border border-honey-200 text-ink hover:border-ink">
                انصراف
              </button>
              <button
                type="submit"
                disabled={saving || uploading}
                className="inline-flex items-center gap-2 bg-honey-500 hover:bg-honey-600 text-white font-display font-bold px-5 py-2.5 rounded-full disabled:opacity-60"
                data-testid="admin-article-save"
              >
                <Save className="w-4 h-4" /> {saving ? "در حال ذخیره..." : "ذخیره"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function F({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <label className="block text-sm font-display text-inkMuted mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 rounded-xl border border-honey-200 bg-white focus:border-honey-500 focus:outline-none"
        data-testid={testid}
      />
    </div>
  );
}
